/**
 * Import-error collection + .xlsx export.
 *
 * The per-column warning triangle in MapperRow surfaces only the *first*
 * invalid value of a column. `collectImportErrors` instead walks every value
 * of every mapped column so the whole error list can be downloaded at once,
 * without the user hovering each warning individually.
 */
import { DataType } from "@/types/enums/DataType";
import { DataTypeDetectionService } from "@/lib/utils/import-dtype-detection";
import {
  isMailOptInField,
  isSmsOptInField,
  resolveImportBooleanCell,
} from "@/lib/utils/import-boolean-words";
import { ImportMapper } from "../types/ImportMapper";
import { TableColumnData } from "../types/TableColumnData";
import { isArrivalNoticeField, parseArrivalNoticeCell } from "../arrivalNotice/arrivalNoticeCells";

export type ImportError = {
  /** 1-based spreadsheet row (header is row 1, data starts at row 2). */
  row: number;
  /** Source column header from the imported file. */
  column: string;
  /** Mapped target field name. */
  field: string;
  /** The offending value. */
  value: string;
  /** Expected data type. */
  dataType: DataType;
  /** Allowed options — only set for SINGLE_SELECT / MULTI_SELECT. */
  options: string[];
  /** W4 Hop B: an arrival-notice cell (a bad address or too many) — its own
   *  reason; the cell is IGNORED, never replaced by a default. */
  arrivalNotice?: true;
};

/**
 * Row-level validity check. Each branch is deliberately matched to the
 * detector that drives the per-column warning triangle, so the downloaded
 * list never disagrees with what the UI shows:
 *  - the `isValidXValue` helpers normalise/trim internally → pass the raw cell;
 *  - DATE uses `isValidDateCell` (strict), NOT the looser `isValidDateValue`;
 *  - SELECT membership is tested against the RAW value, mirroring
 *    `isValidSingleSelect` / `isValidMultiSelect` in input-validator.
 */
function isValidForType(
  dataType: DataType,
  raw: string,
  options: string[],
  optIn = false,
): boolean {
  switch (dataType) {
    case DataType.LONG:
      return DataTypeDetectionService.isValidLongValue(raw);
    case DataType.CURRENCY:
    case DataType.DOUBLE:
      return DataTypeDetectionService.isValidDoubleValue(raw);
    case DataType.BOOLEAN:
      // The row builder's rule: for the mail opt-in an ambiguous word ("jo")
      // is a NO, not an error (audit-14 M-1).
      return resolveImportBooleanCell(raw, null, optIn) !== null;
    case DataType.EMAIL:
      return DataTypeDetectionService.isValidEmailValue(raw);
    case DataType.URL:
      return DataTypeDetectionService.isValidUrlValue(raw);
    case DataType.DATE:
      return DataTypeDetectionService.isValidDateCell(raw);
    case DataType.DATE_TIME:
      return DataTypeDetectionService.isValidDateTimeValue(raw);
    case DataType.OBJECT_ID:
      return DataTypeDetectionService.isValidObjectIdValue(raw);
    case DataType.STRING:
      return DataTypeDetectionService.isValidStringValue(raw);
    case DataType.SINGLE_SELECT:
      return options.includes(raw);
    case DataType.MULTI_SELECT:
      return raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .every((v) => options.includes(v));
    default:
      // No row-level validation for this type (UNKNOWN, ID, …).
      return true;
  }
}

/**
 * Scans every value of every mapped column and returns one entry per invalid
 * cell, sorted by row number.
 */
export function collectImportErrors(
  mappingMap: Map<number, ImportMapper>,
  tableData: TableColumnData[],
): ImportError[] {
  const errors: ImportError[] = [];
  for (const [index, mapper] of mappingMap.entries()) {
    const field = mapper.field;
    if (!field || field.dataType === DataType.UNKNOWN) continue;
    const entry = tableData[index];
    if (!entry) continue;
    const options = field.options ?? [];
    entry.values.forEach((raw, i) => {
      if (raw === null || raw === undefined) return;
      const str = String(raw);
      // Empty / whitespace-only cells are skipped, matching `isEmptyCell` in
      // the detectors and the empty-skip in `isValidSingleSelect`.
      if (str.trim() === "") return;
      // An e-mail cell with nothing left once cleaned (only a zero-width
      // space, a BOM) is an empty cell too — `trim()` does not know those.
      if (
        field.dataType === DataType.EMAIL &&
        DataTypeDetectionService.isEmptyEmailCell(str)
      ) {
        return;
      }
      // The arrival-notice column is free text to the type check but a list
      // of addresses to the import: its own rule (arrivalNoticeCells).
      if (isArrivalNoticeField(field)) {
        if (parseArrivalNoticeCell(str).kind === "invalid") {
          errors.push({
            row: i + 2,
            column: entry.header || field.name,
            field: field.name,
            value: str.trim(),
            dataType: field.dataType,
            options,
            arrivalNotice: true,
          });
        }
        return;
      }
      if (!isValidForType(field.dataType, str, options, isMailOptInField(field))) {
        errors.push({
          row: i + 2, // +1 header row, +1 for 1-based row numbering
          column: entry.header || field.name,
          field: field.name,
          value: str.trim(),
          dataType: field.dataType,
          options,
        });
      }
    });
  }
  return errors.sort((a, b) => a.row - b.row);
}

/**
 * How many cells the import will carry WITHOUT their value — present but
 * unparseable, and no usable column default to stand in (the step-3
 * "values dropped" notice). EMAIL and URL as before; BOOLEAN since a BOOLEAN
 * column's default is applied (audit-14 H-1): a "vielleicht" with no default
 * imports as unset, and for the mail opt-in unset is not a NO.
 */
export function countDroppedImportValues(
  mappingMap: Map<number, ImportMapper>,
  tableData: TableColumnData[],
): number {
  let count = 0;
  for (const [index, mapper] of mappingMap.entries()) {
    const field = mapper.field;
    if (!field || field.dataType === DataType.UNKNOWN) continue;
    const tableEntry = index < tableData.length ? tableData[index] : null;
    if (!tableEntry) continue;
    const defaultValue = typeof mapper.value === "string" ? mapper.value : "";

    let dropped: (v: string) => boolean;
    if (field.dataType === DataType.BOOLEAN) {
      const optIn = isMailOptInField(field);
      // The SMS opt-in's unknown word never takes the default (audit r1 C8),
      // so the notice counts it as dropped — what the row builder does.
      const strict = isSmsOptInField(field);
      dropped = (v) => resolveImportBooleanCell(v, defaultValue, optIn, strict) === null;
    } else if (
      field.dataType === DataType.EMAIL ||
      field.dataType === DataType.URL
    ) {
      const isValid =
        field.dataType === DataType.EMAIL
          ? (v: string) => DataTypeDetectionService.isValidEmailValue(v)
          : (v: string) => DataTypeDetectionService.isValidUrlValue(v);
      if (isValid(defaultValue)) continue;
      // An e-mail cell with nothing left once cleaned (only a zero-width
      // space, a BOM) is an empty cell, nothing to drop (address wave, P0).
      dropped =
        field.dataType === DataType.EMAIL
          ? (v) => !DataTypeDetectionService.isEmptyEmailCell(v) && !isValid(v)
          : (v) => !isValid(v);
    } else {
      continue;
    }

    for (const val of tableEntry.values) {
      if (!val) continue;
      if (dropped(val)) count++;
    }
  }
  return count;
}

export type ImportErrorListLabels = {
  row: string;
  column: string;
  field: string;
  value: string;
  reason: string;
};

/**
 * What XML 1.0 cannot carry: C0 controls other than tab / LF / CR, U+FFFE /
 * U+FFFF and a lone surrogate half. The values come straight out of an
 * uploaded sheet, and one such character in a cell makes Excel reject the
 * whole file as damaged.
 */
const XML_UNSAFE =
  // eslint-disable-next-line no-control-regex -- control chars are what it strips
  /[\x00-\x08\x0B\x0C\x0E-\x1F\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/** Excel's limit for one cell. */
const XLSX_MAX_CELL_LENGTH = 32767;

/**
 * One text cell. Line breaks become a space (one row per invalid cell, the
 * value as the mapper's warning shows it), characters the file format cannot
 * hold are dropped, and an over-long value is cut to Excel's cell limit.
 */
function sheetText(value: string): string {
  return value
    .replace(/\r\n|\r|\n/g, " ")
    .replace(XML_UNSAFE, "")
    .slice(0, XLSX_MAX_CELL_LENGTH);
}

/**
 * The error list as sheet rows: the header, then one row per invalid cell —
 * the row number as a number, everything else as text.
 */
export function importErrorSheetRows(
  errors: ImportError[],
  labels: ImportErrorListLabels,
  resolveReason: (error: ImportError) => string,
): (string | number)[][] {
  const rows: (string | number)[][] = [
    [labels.row, labels.column, labels.field, labels.value, labels.reason].map(sheetText),
  ];
  for (const e of errors) {
    rows.push([
      e.row,
      sheetText(e.column),
      sheetText(e.field),
      sheetText(e.value),
      sheetText(resolveReason(e)),
    ]);
  }
  return rows;
}

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * A worksheet name Excel accepts: none of `[ ] : * ? / \`, at most 31
 * characters, not empty.
 */
function worksheetName(name: string): string {
  return name.replace(/[[\]:*?/\\]/g, "_").slice(0, 31).trim() || "Import";
}

/**
 * Builds the error list as an .xlsx (board M-36: a comma CSV opens as ONE
 * column in a German Excel). Every value goes in as a text cell, never a
 * formula, so "=HYPERLINK(…)" from the uploaded sheet shows as text and runs
 * nothing — no CSV-style "'" guard needed. Header bold, frozen and
 * filterable. exceljs is loaded on demand, as for the import itself.
 */
export async function buildImportErrorXlsx(
  errors: ImportError[],
  labels: ImportErrorListLabels,
  resolveReason: (error: ImportError) => string,
  sheetName: string,
): Promise<Blob> {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(worksheetName(sheetName), {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  for (const row of importErrorSheetRows(errors, labels, resolveReason)) {
    sheet.addRow(row);
  }
  sheet.getRow(1).font = { bold: true };
  [8, 24, 22, 40, 70].forEach((width, i) => {
    sheet.getColumn(i + 1).width = width;
  });
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: 5 } };
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: XLSX_MIME });
}

/**
 * The `warningImport` message that explains why a cell of this type is
 * invalid — the sentence the mapper's warning triangle shows. CURRENCY is
 * checked as a decimal number (`isValidForType`), so it reads as one; a type
 * without its own sentence gets the generic one instead of a raw key.
 */
export type ImportErrorReasonKey =
  | "isInvalid-LONG"
  | "isInvalid-DOUBLE"
  | "isInvalid-BOOLEAN"
  | "isInvalid-EMAIL"
  | "isInvalid-URL"
  | "isInvalid-DATE"
  | "isInvalid-DATE_TIME"
  | "isInvalid-SINGLE_SELECT"
  | "isInvalid-MULTI_SELECT"
  | "isInvalid-STRING"
  | "isInvalid-OBJECT_ID"
  | "isInvalid-UNKNOWN";

export function importErrorReasonKey(dataType: DataType): ImportErrorReasonKey {
  switch (dataType) {
    case DataType.LONG:
      return "isInvalid-LONG";
    case DataType.DOUBLE:
    case DataType.CURRENCY:
      return "isInvalid-DOUBLE";
    case DataType.BOOLEAN:
      return "isInvalid-BOOLEAN";
    case DataType.EMAIL:
      return "isInvalid-EMAIL";
    case DataType.URL:
      return "isInvalid-URL";
    case DataType.DATE:
      return "isInvalid-DATE";
    case DataType.DATE_TIME:
      return "isInvalid-DATE_TIME";
    case DataType.SINGLE_SELECT:
      return "isInvalid-SINGLE_SELECT";
    case DataType.MULTI_SELECT:
      return "isInvalid-MULTI_SELECT";
    case DataType.STRING:
      return "isInvalid-STRING";
    case DataType.OBJECT_ID:
      return "isInvalid-OBJECT_ID";
    default:
      return "isInvalid-UNKNOWN";
  }
}

/** Characters a file name cannot carry on Windows/macOS, and control chars. */
// eslint-disable-next-line no-control-regex -- control chars are what it strips
const UNSAFE_FILE_CHARS = /[\u0000-\u001F\u007F:*?"<>|]/g;

/**
 * Download name of the error list: `<prefix>-<source file>-<YYYY-MM-DD>.xlsx`
 * (local day), e.g. "import-fehler-Gästeliste Spa-2026-10-24.xlsx" — so two
 * lists from two files, or two days, do not land as "import-fehler (3).xlsx".
 * The source name loses its folder and extension; without one the name is
 * prefix and day only.
 */
export function importErrorFileName(
  prefix: string,
  sourceFileName: string,
  now: Date = new Date(),
): string {
  const baseName = (sourceFileName ?? "").split(/[\\/]/).pop() ?? "";
  const stem = baseName
    .replace(/\.[^.]*$/, "")
    .replace(UNSAFE_FILE_CHARS, "_")
    .trim()
    .slice(0, 80)
    .trim();
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return [prefix, stem, day].filter(Boolean).join("-") + ".xlsx";
}
